<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import BaseModal from '@/components/modal/BaseModal.vue';
import ModalFooterConfirmButtons from '@/components/modal/ModalFooterConfirmButtons.vue';
import { useModalStore } from '@/stores/modal';

interface Props {
  src: string;
  filename?: string;
  alt?: string;
  errorMessage?: string;
}

const props = defineProps<Props>();
const modalStore = useModalStore();

const isLoading = ref(true);
const hasError = ref(false);

const handleClose = () => {
  modalStore.close();
};

const onLoad = () => {
  isLoading.value = false;
};

const onError = () => {
  isLoading.value = false;
  hasError.value = true;
};

watch(
  () => props.src,
  () => {
    isLoading.value = true;
    hasError.value = false;
  },
);

onMounted(() => {
  isLoading.value = true;
});
</script>

<template>
  <BaseModal title="이미지 보기" width="800px" aria-label="이미지 뷰어" @close="handleClose">
    <div class="viewer-body" :aria-busy="isLoading" aria-live="polite">
      <!-- 파일명 표시 (제목 아래 작은 글씨) -->
      <div v-if="filename" class="viewer-filename" :title="filename">
        <svg class="file-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <polyline points="13 2 13 9 20 9" />
        </svg>
        <span class="filename-text">{{ filename }}</span>
      </div>

      <div class="viewer-canvas" role="region" aria-label="이미지 표시 영역">
        <template v-if="!hasError && !props.errorMessage">
          <img
            :src="props.src"
            :alt="props.alt || props.filename || '첨부 이미지'"
            class="viewer-image"
            @load="onLoad"
            @error="onError"
          />
        </template>
        <template v-else>
          <div class="viewer-error" role="alert">
            <svg class="error-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{{ props.errorMessage || '파일을 찾을 수 없습니다' }}</span>
          </div>
        </template>
        <div v-if="isLoading" class="viewer-loading">
          <div class="loading-spinner"></div>
          <span>이미지 로딩 중...</span>
        </div>
      </div>
    </div>
    <template #footer>
      <ModalFooterConfirmButtons :confirm-text="'닫기'" :show-cancel="false" @confirm="handleClose" />
    </template>
  </BaseModal>
</template>

<style scoped>
.viewer-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 파일명 표시 영역 */
.viewer-filename {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-family: 'NanumSquare', sans-serif;
  
  .file-icon {
    flex-shrink: 0;
    color: #6b7280;
    width: 16px;
    height: 16px;
  }
  
  .filename-text {
    font-size: 13px;
    font-weight: 500;
    color: #404040;
    letter-spacing: -0.3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0; /* flex item이 줄어들 수 있도록 */
  }
}

.viewer-canvas {
  position: relative;
  width: 100%;
  height: 600px;
  max-height: calc(80vh - 100px);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.viewer-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: opacity 0.3s ease;
}

.viewer-loading {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #ffffff;
  font-family: 'NanumSquare', sans-serif;
  font-size: 14px;
  font-weight: 500;
  
  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 255, 255, 0.2);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

.viewer-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #ee8544;
  font-family: 'NanumSquare', sans-serif;
  font-size: 14px;
  font-weight: 500;
  background: rgba(238, 133, 68, 0.1);
  padding: 24px;
  border-radius: 8px;
  text-align: center;
  
  .error-icon {
    color: #ee8544;
    width: 32px;
    height: 32px;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .viewer-canvas {
    height: 60vh;
    max-height: calc(70vh - 120px);
    border-radius: 8px;
  }
  
  .viewer-filename {
    padding: 8px 10px;
    
    .file-icon {
      width: 14px;
      height: 14px;
    }
    
    .filename-text {
      font-size: 12px;
    }
  }
}
</style>